/**
 * ToastContext — lightweight global toast system.
 *
 * Follows the project banner style (matches DeviceEnrollBanner visuals):
 *  - useTheme() → { T, C } for ALL colors — NO hardcoded hex
 *  - Inline styles for every color/shadow/border
 *  - Animation: mi-anim-slide-up from src/index.css
 *
 * API:
 *   const { showToast } = useToast();
 *   showToast('mensaje', { variant: 'warning', duration: 4000 });
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  variant?: ToastVariant;
  /** Time in ms before the toast auto-dismisses. Default: 4000. Pass 0 to keep it until manually dismissed. */
  duration?: number;
  /**
   * Optional stable id to dedupe. If a toast with the same id is already
   * visible, its timer is reset instead of enqueuing a duplicate.
   */
  id?: string;
}

interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let _autoId = 0;
const nextId = () => `toast-${++_autoId}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const scheduleDismiss = useCallback((id: string, duration: number) => {
    if (duration <= 0) return;
    const timer = setTimeout(() => dismissToast(id), duration);
    timersRef.current.set(id, timer);
  }, [dismissToast]);

  const showToast = useCallback<ToastContextValue['showToast']>((message, options) => {
    const variant = options?.variant ?? 'info';
    const duration = options?.duration ?? 4000;
    const id = options?.id ?? nextId();

    setToasts((prev) => {
      const existingIdx = prev.findIndex((t) => t.id === id);
      if (existingIdx !== -1) {
        const next = prev.slice();
        next[existingIdx] = { id, message, variant, duration };
        return next;
      }
      return [...prev, { id, message, variant, duration }];
    });

    // Reset (or set) the auto-dismiss timer.
    const existingTimer = timersRef.current.get(id);
    if (existingTimer) clearTimeout(existingTimer);
    timersRef.current.delete(id);
    scheduleDismiss(id, duration);

    return id;
  }, [scheduleDismiss]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/* --------------------------- Viewport / Item UI --------------------------- */

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 520,
        width: 'calc(100vw - 32px)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastItemView key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItemView({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const { C, T } = useTheme();

  const accentColor =
    toast.variant === 'success' ? C.success
    : toast.variant === 'warning' ? C.danger
    : toast.variant === 'danger' ? C.danger
    : C.primary;

  return (
    <div
      role={toast.variant === 'danger' || toast.variant === 'warning' ? 'alert' : 'status'}
      className="mi-anim-slide-up"
      style={{
        pointerEvents: 'auto',
        background: T.surfaceBg,
        border: `1px solid ${accentColor}38`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 12,
        boxShadow: T.shadow,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: `${accentColor}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>

      <span
        style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 500,
          color: T.text,
          lineHeight: 1.4,
        }}
      >
        {toast.message}
      </span>

      <button
        type="button"
        aria-label="Cerrar"
        onClick={() => onDismiss(toast.id)}
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: 'none',
          background: `${T.textMuted}22`,
          cursor: 'pointer',
          color: T.textMuted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
